import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CitiesPlayedModal from '../profile/modal/CitiesPlayedModal';
import MemberProfileSkeleton from '../members/MemberProfileSkeleton';
import NetworkTab from '../profile/tabs/NetworkTab';
import PostsTab from '../profile/tabs/PostsTab';
import ReviewsSection from './ReviewsSection';
import ProfileTabs from './ProfileTabs';
import AboutTab from './AboutTab';
import ProfileHeader from '../profile/sections/ProfileHeader';
import ProfileStats from './ProfileStats';
import { ClubMembershipsCard } from './ClubMembershipsCard';
import { useProfile } from '../../hooks/useProfile';
import { storage } from '../../lib/supabase/storage';
import { supabase } from '@/lib/supabase';
import { useConnectionStore } from '../../store/connectionStore';
import { fetchCityImageFromMultipleSources } from '@/lib/fetchCityImage';
import { Post } from '../../types/post';
import { Member } from '../../types/member';
import { useTheme } from '@/contexts/ThemeContext';
import { normalizeCity } from '@/lib/city/normalizer';

interface ProfileSectionProps {
  defaultTab?: string;
  hideDetails?: boolean;
  onTabChange?: (tab: string) => void;
}

const ProfileSection: React.FC<ProfileSectionProps> = ({
  defaultTab = 'about',
  hideDetails = false,
  onTabChange
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // 1. Extract & normalize city
  const cityParam = searchParams.get('city');
  const normalizedCity = cityParam ? normalizeCity(cityParam) : '';

  const urlTab = searchParams.get('tab');
  const initialTab = (urlTab || defaultTab) as string;

  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [memberPosts, setMemberPosts] = useState<Post[]>([]);
  const [overallRating, setOverallRating] = useState(0);
  const [pendingClubInvites, setPendingClubInvites] = useState(0);
  const isLoading = useConnectionStore((state) => state.isLoading);

  const {
    profile,
    isLoading: profileLoading,
    error: profileError,
    fetchProfile,
    updateProfile,
  } = useProfile();

  const { pendingRequests, fetchConnections } = useConnectionStore();
  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  const { isDarkMode } = useTheme();

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    const newTabParam = new URLSearchParams(location.search).get('tab');
    if (newTabParam && newTabParam !== activeTab) {
      setActiveTab(newTabParam);
      onTabChange?.(newTabParam);
    }
  }, [location.search]);

  useEffect(() => {
    if (profile?.id) {
      fetchOverallRating(profile.id);
    }
  }, [profile?.id]);

  useEffect(() => {
    // Fetch pending club invitations count
    const fetchPendingInvitations = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: userData } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (!userData) return;

        const { count } = await supabase
          .from('club_invitations')
          .select('*', { count: 'exact', head: true })
          .eq('invitee_id', userData.id)
          .eq('status', 'pending');

        setPendingClubInvites(count || 0);
      } catch (error) {
        console.error('Error fetching pending club invitations:', error);
      }
    };

    fetchPendingInvitations();
  }, []);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchOverallRating = async (userId: string) => {
    try {
      const { data: ratingsData, error: ratingsError } = await supabase
        .from('user_ratings')
        .select('overall_rating')
        .eq('user_id', userId)
        .maybeSingle();
      if (ratingsError) throw ratingsError;
      setOverallRating(
        ratingsData?.overall_rating
          ? parseFloat(ratingsData.overall_rating.toFixed(1))
          : 0
      );
    } catch (err) {
      console.error('[ProfileSection] rating error', err);
    }
  };

  const socialLinks = {
    linkedinUrl: profile?.linkedin_url || null,
    facebookUrl: profile?.facebook_url || null,
    instagramUrl: profile?.instagram_url || null,
  };

  // 2. Build currentUser with the normalized city
  const currentUser: Member = {
    id: profile?.id || '',
    name: profile?.full_name || 'User',
    image:
      profile?.avatar_url?.startsWith('data:image')
        ? 'https://...fallback.png'
        : profile?.avatar_url || 'https://...fallback.png',
    city: normalizedCity || profile?.city || 'Location',
    jobTitle: profile?.job_type || 'Role',
    company: profile?.company || 'Company',
    skillLevel: 'Advanced',
    connections: 0,
    matchesPlayed: 0,
    citiesPlayed: ['Valencia', 'London', 'Paris'],
    achievements: ['Tournament Winner', 'Elite Status'],
    rating: overallRating,
    joinDate: '2024-10-01',
    industry: 'Technology',
    points: {
      matches: 6200,
      training: 4500,
      connections: 16200,
      cities: 1800,
      events: 3200,
      achievements: 5000,
      total: 36900,
    },
  };
  const [cityImage, setCityImage] = useState<string | null>(null);
  useEffect(() => {
    const loadCityImage = async () => {
      if (currentUser.city) {
        try {
          const url = await fetchCityImageFromMultipleSources(currentUser.city);
          setCityImage(url);
        } catch {
          setCityImage(
            'https://images.pexels.com/photos/3052361/pexels-photo-3052361.jpeg'
          );
        }
      }
    };
    loadCityImage();
  }, [currentUser.city]);

  if (isLoading || profileLoading) {
    return <MemberProfileSkeleton />;
  }
  if (profileError) {
    return (
      <div className="bg-red-500/10 text-red-500 p-4 rounded-xl border">
        <p>{profileError}</p>
        <button onClick={fetchProfile} className="underline mt-2">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProfileHeader
        member={currentUser}
        profile={profile}
        cityImage={cityImage!}
        socialLinks={socialLinks}
        onImageChange={async (file) => {
          const uploadData = await storage.uploadFile({
            bucket: 'avatars',
            file,
            path: `avatars/${currentUser.id}/${file.name}`,
          });
          if (uploadData.url) {
            await updateProfile({ avatar_url: `${uploadData.url}?t=${Date.now()}` });
            fetchProfile();
          }
        }}
      />

      <ProfileStats
        member={currentUser}
        onShowConnections={() => setActiveTab('network')}
        onShowReviews={() => setActiveTab('ratings')}
      />

      <ProfileTabs
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          onTabChange?.(tab);
        }}
        pendingCount={pendingClubInvites}
        pendingConnectionsCount={pendingRequests?.length || 0}
      />

      {activeTab === 'about' && <AboutTab member={currentUser} profile={profile} />}
      {activeTab === 'posts' && <PostsTab posts={memberPosts} memberName={currentUser.name} />}
      {activeTab === 'network' && <NetworkTab />}
      {activeTab === 'clubs' && <ClubMembershipsCard />}
      {activeTab === 'ratings' && currentUser.id && (
        <ReviewsSection memberId={currentUser.id} memberName={currentUser.name} sports={[]} />
      )}
    </div>
  );
};

export default ProfileSection;
